import React, { useMemo } from 'react';
import type { ComponentPropsWithRef, ElementType, ReactNode } from 'react';

import PropTypes from 'prop-types';

import {
  mergeProps,
  useInstanceId,
  variantSwitch,
} from '@adsk/alloy-react-helpers';
import { AlertWarningFilledIcon } from '@adsk/alloy-react-icon';
import { useTheme } from '@adsk/alloy-react-styles';
import { stylePropType, StylableComponent } from '@adsk/alloy-react-theme';
import Tooltip, {
  InfoPopover,
  InfoPopoverProps,
} from '@adsk/alloy-react-tooltip';

const FORM_FIELD_ERROR_VARIANTS = {
  CAPTION: 'caption',
  TOOLTIP: 'tooltip',
};

type DefaultInfoPopoverProps = Omit<InfoPopoverProps, 'message'>;

const getTooltipTargetProps = () => ({ shouldWrapChildren: false });

type ErrorVariants = typeof FORM_FIELD_ERROR_VARIANTS;
export type ErrorVariant = ErrorVariants[keyof ErrorVariants];

const LABEL_VARIANTS = {
  SIDE: 'side',
  TOP: 'top',
};

type LabelVariants = typeof LABEL_VARIANTS;
export type LabelVariant = LabelVariants[keyof LabelVariants];

export type RenderInfoPopover = (
  defaultProps: DefaultInfoPopoverProps,
) => ReactNode;

export type FormFieldProps = StylableComponent<HTMLDivElement> & {
  error?: ReactNode;
  errorVariant?: ErrorVariant;
  label?: ReactNode;
  labelVariant?: LabelVariant;
  required?: boolean;
  renderInfoPopover?: RenderInfoPopover;
};

/**
 * A FormField component which provides error message displaying.
 */
const FormField = React.forwardRef<HTMLDivElement, FormFieldProps>(
  (
    {
      className,
      style,
      error,
      errorVariant = FORM_FIELD_ERROR_VARIANTS.CAPTION,
      label,
      labelVariant = LABEL_VARIANTS.SIDE,
      required,
      children,
      renderInfoPopover,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    const formFieldTokens = theme.tokens?.component?.formField;
    const id = useInstanceId();

    const defaultInfoPopoverProps = useMemo<DefaultInfoPopoverProps>(
      () => ({
        variant: InfoPopover.VARIANTS.LIGHT,
        style: formFieldTokens?.infoPopover,
        placement: InfoPopover.PLACEMENTS.TOP,
      }),
      [formFieldTokens],
    );

    const labelId = `FormFieldLabel${id}`;
    const errorId = `FormFieldError${label}`;

    return (
      <div
        {...props}
        ref={ref}
        className={className}
        css={[
          {
            display: 'flex',
            width: '100%',
          },
          variantSwitch({
            statement: labelVariant,
            variants: {
              [LABEL_VARIANTS.TOP]: {
                flexDirection: 'column',
                alignItems: 'flex-start',
              },
            },
          }),
          style,
        ]}
      >
        {label && (
          <label
            id={labelId}
            data-testid="FormField__label"
            css={[
              formFieldTokens?.label?._,
              variantSwitch({
                statement: labelVariant,
                variants: {
                  [LABEL_VARIANTS.SIDE]: formFieldTokens?.label?.side,
                  [LABEL_VARIANTS.TOP]: formFieldTokens?.label?.top,
                },
              }),
            ]}
          >
            <span dir="auto">{label}</span>
            {required && <span css={[formFieldTokens?.required]}>*</span>}
            {renderInfoPopover?.(defaultInfoPopoverProps)}
          </label>
        )}
        <div
          css={[
            {
              display: 'flex',
              width: '100%',
              '&>*': {
                flex: '1 1 auto',
              },
            },
            variantSwitch({
              statement: errorVariant,
              variants: {
                [FORM_FIELD_ERROR_VARIANTS.CAPTION]: {
                  flexDirection: 'column',
                },
                [FORM_FIELD_ERROR_VARIANTS.TOOLTIP]: {
                  alignItems: 'center',
                },
              },
            }),
          ]}
        >
          <div>
            {React.isValidElement(children)
              ? React.cloneElement<ComponentPropsWithRef<ElementType>>(
                  children,
                  mergeProps(
                    {
                      'aria-labelledby': labelId,
                      'aria-invalid': error ? true : undefined,
                      'aria-errormessage': error ? errorId : undefined,
                      'aria-required': required ? true : undefined,
                      required,
                    },
                    children.props as Record<string, unknown>,
                  ) as unknown as Partial<ComponentPropsWithRef<ElementType>>,
                )
              : children}
          </div>
          {error && errorVariant === FORM_FIELD_ERROR_VARIANTS.TOOLTIP && (
            <div
              id={errorId}
              role="alert"
              data-testid="FormField__toolTip"
              css={[formFieldTokens?.error?.tooltip]}
            >
              <Tooltip
                getTargetProps={getTooltipTargetProps}
                content={<span dir="auto">{error}</span>}
                placement={Tooltip.PLACEMENTS.TOP}
              >
                <AlertWarningFilledIcon size={16} />
              </Tooltip>
            </div>
          )}
          {error && errorVariant === FORM_FIELD_ERROR_VARIANTS.CAPTION && (
            <div
              id={errorId}
              role="alert"
              data-testid="FormField__error"
              css={[formFieldTokens?.error?.container]}
            >
              <AlertWarningFilledIcon
                size={16}
                style={formFieldTokens?.alertIcon}
              />
              <span dir="auto" css={[formFieldTokens?.error?.text]}>
                {error}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  },
);

FormField.displayName = 'FormField';

FormField.propTypes = {
  /** Child input component(s) */
  children: PropTypes.any,
  /** The error message to display */
  error: PropTypes.any,
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The error variant */
  errorVariant: PropTypes.oneOf(Object.values(FORM_FIELD_ERROR_VARIANTS)),
  /** The text for the label.  This text is also used to set the aria-label prop on the provided children */
  label: PropTypes.any,
  /** The label variant */
  labelVariant: PropTypes.oneOf(Object.values(LABEL_VARIANTS)),
  /** Field is required */
  required: PropTypes.bool,
  /** Render function for info popover that receives default props and renders InfoPopover */
  renderInfoPopover: PropTypes.func,
};

export default Object.assign(FormField, {
  ERROR_VARIANTS: FORM_FIELD_ERROR_VARIANTS,
  LABEL_VARIANTS,
  INFO_POPOVER_VARIANTS: InfoPopover.VARIANTS,
});
